// Одна переписка: лента сообщений, что из неё выяснено, что передано человеку и чем ответить.
//
// ## Помощник тут не самостоятелен, и экран обязан это показывать
//
// Смысловое ядро сценариев 96-101: наружу от имени компании ничего не уходит само по себе,
// пока режим — «предлагает ответ». Поэтому у каждого сообщения виден автор (сценарий 101), а
// предложенный ответ живёт отдельной полосой над полем ввода, с двумя кнопками и объяснённым
// последствием у каждой.
//
// ## Три причины тишины, которые НЕЛЬЗЯ слить в одну (правило 7)
//
// «Предложений нет» может означать три разных вещи, и человек по ним принимает три разных
// решения:
//
//   1. помощник ВЫКЛЮЧЕН на этой учётной записи — он не ответит никогда, пока режим не сменят;
//   2. диалог НА ПАУЗЕ, потому что в него написал человек — помощник молчит до явного
//      возврата, и вернуть его может только нажатие;
//   3. предложенных ответов сейчас ПРОСТО НЕТ — всё в порядке, ждём сообщения соискателя.
//
// Слипание этих трёх в одну серую фразу «помощник ничего не предлагает» отправило бы рекрутёра
// ждать ответа, которого не будет. Поэтому у каждой свой вид, свой текст и своё действие.
//
// ## Чего в ответе сервера не хватает
//
// Имени соискателя в переписке нет: есть `external_chat_id`, опознаватель чата на площадке.
// Он стоит мелко в шапке — там, где он и нужен, при разборе с площадкой.

import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  RiArrowGoBackLine,
  RiChat3Line,
  RiChatOffLine,
  RiErrorWarningLine,
  RiPauseCircleLine,
  RiRobot2Line,
  RiSettings3Line,
  RiUserSearchLine,
} from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import * as Alert from '@/shared/ui/alert';
import * as Badge from '@/shared/ui/badge';
import { Block } from '@/shared/ui/block';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { FieldRow } from '@/shared/ui/field-row';
import * as Hint from '@/shared/ui/hint';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { TextArea } from '@/shared/ui/text-area';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';
import { cn } from '@/utils/cn';

import type {
  AssistantSettings,
  DialogField,
  DialogHandoff,
  DialogLog,
  DialogMessage,
  DialogRow,
  MessageState,
} from '../api/dialogs';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import {
  useApproveMessageMutation,
  useAssistantSettingsQuery,
  useDialogQuery,
  useDiscardMessageMutation,
  useResumeAssistantMutation,
  useDialogsQuery,
  useScriptQuery,
  useSendMessageMutation,
} from '../queries';
import type { HiringRoute } from '../route';
import {
  FILTER_ACCOUNT,
  FILTER_STATUS,
  PANEL_ASSISTANT_SETTINGS,
  hiringHref,
  openPanelHref,
} from '../route';
import { dialogStatusColor } from '../views/DialogsList';

/** Вести переписку руками и решать по предложенным ответам. */
const OPERATE = 'hiring.assistant.operate';
/** Менять режим помощника и вести библиотеку ответов. */
const CONFIGURE = 'hiring.assistant.configure';

/** Кто пишет от имени компании: у этих сообщений состояние отправки что-то значит. */
const НАШИ_АВТОРЫ: ReadonlySet<string> = new Set(['assistant', 'operator']);

/** Оттенок состояния сообщения. Название приходит с сервера полем `state_title`. */
const СОСТОЯНИЕ_ЦВЕТ: Readonly<Record<MessageState, 'gray' | 'blue' | 'green' | 'orange' | 'red'>> =
  {
    proposed: 'orange',
    approved: 'blue',
    sent: 'green',
    failed: 'red',
    rejected_by_guard: 'red',
    discarded: 'gray',
  };

/**
 * Что означает состояние, которого человек не ждал. У обычной отправки пояснения нет: она и
 * так понятна, а фраза под каждым сообщением превратилась бы в узор.
 */
const СОСТОЯНИЕ_ПОЯСНЕНИЕ: Readonly<Partial<Record<MessageState, string>>> = {
  failed: 'Площадка сообщение не приняла: соискатель его не получил.',
  rejected_by_guard:
    'В тексте нашлось то, чего помощнику говорить нельзя. Наружу не ушло вовсе, диалог передан человеку.',
  discarded: 'Отклонено человеком: соискатель этого текста не видел.',
};

export function DialogDetail({ route }: { route: HiringRoute }) {
  // Тот же запрос, что и у списка слева: react-query отдаёт его из кэша, второго обращения к
  // серверу нет. Нужен он ровно затем, чтобы деталь ЗНАЛА, что выбирать нечего.
  //
  // До 04.09.2026 два пустых состояния стояли рядом и говорили разное об одном и том же:
  // слева «Переписок пока нет», справа одновременно «Выберите переписку в списке слева».
  // Человек читает это как поломку — список сломался или он чего-то не видит, — и идёт
  // спрашивать в чат вместо того, чтобы прочитать ответ на экране.
  const status = route.search.filters[FILTER_STATUS];
  const accountId = route.search.filters[FILTER_ACCOUNT];
  const список = useDialogsQuery({ status, account_id: accountId });
  const пусто = список.data !== undefined && список.data.items.length === 0;

  if (route.rowId === undefined) {
    return (
      <DetailPane>
        <div className='flex h-full items-center justify-center'>
          {пусто ? (
            // Слева уже написано, что переписок нет и откуда они берутся. Повторять это
            // здесь — сказать один факт дважды: человек читает повтор как два разных
            // утверждения и ищет между ними разницу. Правая колонка называет СЕБЯ: что здесь
            // появится, когда появится первая переписка.
            <EmptyState
              icon={RiChat3Line}
              title='Здесь будет лента переписки'
              description='Кто и что писал, что помощник выяснил о соискателе, что передано человеку, и поле, чтобы ответить самому.'
            />
          ) : (
            <EmptyState
              icon={RiChat3Line}
              title='Переписка не выбрана'
              description='Выберите переписку в списке слева — здесь откроется вся её лента: кто и что писал, что помощник уже выяснил о соискателе, что передано человеку, и поле, чтобы ответить самому.'
            />
          )}
        </div>
      </DetailPane>
    );
  }
  return <DialogCard dialogId={route.rowId} route={route} />;
}

function DialogCard({ dialogId, route }: { dialogId: string; route: HiringRoute }) {
  const navigate = useNavigate();
  const query = useDialogQuery(dialogId);
  const canSeeCandidates = useCan('hiring.candidate.view');

  const диалог = query.data?.диалог;

  return (
    <DetailPane
      title={
        <span className='flex min-w-0 items-center gap-2'>
          <RiChat3Line className='size-5 shrink-0 text-text-soft-400' />
          <span className='shrink-0'>Соискатель</span>
          {диалог !== undefined && (
            <Badge.Root
              variant='lighter'
              color={dialogStatusColor(диалог.status)}
              size='medium'
              className='shrink-0'
            >
              {диалог.status_title}
            </Badge.Root>
          )}
          {/* Учётная запись и опознаватель чата — мелким и последними: они нужны при разборе
              с площадкой, а не при чтении переписки, и обрезаться должны первыми. */}
          {диалог !== undefined && (
            <span className='min-w-0 truncate text-paragraph-xs text-text-sub-600'>
              {диалог.account_title} · чат {диалог.external_chat_id}
            </span>
          )}
        </span>
      }
      actions={
        диалог === undefined ? undefined : (
          <>
            {/* Ссылка на карточку кандидата показывается только тем, кому карточки открыты:
                кнопка, за которой ждёт отказ, — это тот же отказ, только на нажатие позже. */}
            {диалог.candidate_id !== null && canSeeCandidates && (
              <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
                <Link
                  to={hiringHref('candidates', { filters: {}, panels: [] }, диалог.candidate_id)}
                >
                  <Button.Icon as={RiUserSearchLine} />
                  Карточка кандидата
                </Link>
              </Button.Root>
            )}
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() =>
                void navigate(openPanelHref(route, PANEL_ASSISTANT_SETTINGS, диалог.account_id))
              }
            >
              <Button.Icon as={RiSettings3Line} />
              Режим помощника
            </Button.Root>
          </>
        )
      }
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='переписка с соискателем'
        isEmpty={() => false}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-3 p-5'>
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-32 w-full' />
          </div>
        }
        empty={null}
      >
        {/* `key` по диалогу обязателен. Без него React переиспользует ту же ветку при
            переходе на соседнюю переписку, и недописанный ответ ОДНОМУ соискателю остаётся
            в поле, открытом для ДРУГОГО, — на один промах по кнопке от чужого сообщения. */}
        {(журнал) => <DialogBody key={журнал.диалог.id} журнал={журнал} route={route} />}
      </ScreenState>
    </DetailPane>
  );
}

function DialogBody({ журнал, route }: { журнал: DialogLog; route: HiringRoute }) {
  const navigate = useNavigate();
  const диалог = журнал.диалог;

  const canOperate = useCan(OPERATE);
  const canConfigure = useCan(CONFIGURE);
  const настройки = useAssistantSettingsQuery();
  // Названия шагов сценария нужны, чтобы вместо машинного кода поля показать вопрос, которым
  // это поле заполняют. Запрос общий с библиотекой ответов и живёт пять минут в кэше.
  const шаги = useScriptQuery(undefined);

  const подтвердить = useApproveMessageMutation(диалог.id);
  const отклонить = useDiscardMessageMutation(диалог.id);
  const написать = useSendMessageMutation(диалог.id);
  const вернуть = useResumeAssistantMutation(диалог.id);

  const [текст, setТекст] = useState('');
  const [ошибкаТекста, setОшибкаТекста] = useState<string | undefined>(undefined);
  const [заметка, setЗаметка] = useState<string | null>(null);

  // Предложенное наружу ещё не ушло, поэтому в ленте его нет: рядом с отправленными оно
  // читалось бы как сказанное соискателю. Живёт оно ровно там, где по нему принимают
  // решение, — полосой над полем ввода.
  const предложения = журнал.сообщения.filter((сообщение) => сообщение.state === 'proposed');
  const лента = журнал.сообщения.filter((сообщение) => сообщение.state !== 'proposed');
  const служебныеЕсть = лента.some((сообщение) => сообщение.служебное_площадки);

  // Лента открывается на последнем сообщении, как любая переписка: иначе человек, открыв
  // диалог на сорок сообщений, видит его начало полугодовой давности и ищет конец руками.
  const область = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const узел = область.current;
    if (узел !== null) {
      узел.scrollTop = узел.scrollHeight;
    }
  }, [диалог.id, лента.length]);

  function решитьПоПредложению(
    действие: typeof подтвердить | typeof отклонить,
    messageId: string,
    занято: string,
  ) {
    if (!canOperate) {
      setЗаметка(
        'Решать по ответам помощника может тот, кто ведёт переписку. Право выдаёт администратор системы.',
      );
      return;
    }
    if (подтвердить.isPending || отклонить.isPending) {
      setЗаметка(занято);
      return;
    }
    setЗаметка(null);
    действие.mutate(messageId);
  }

  function отправитьСвой(событие: FormEvent<HTMLFormElement>) {
    событие.preventDefault();
    if (!canOperate) {
      setЗаметка(
        'Писать соискателю может тот, кто ведёт переписку. Право выдаёт администратор системы.',
      );
      return;
    }
    if (написать.isPending) {
      setЗаметка('Сообщение уже уходит на площадку, ждём ответ. Второй раз не отправляем.');
      return;
    }
    const очищенный = текст.trim();
    if (очищенный === '') {
      setОшибкаТекста('Напишите текст: пустое сообщение соискателю не уходит.');
      return;
    }
    setОшибкаТекста(undefined);
    setЗаметка(null);
    написать.mutate(очищенный, { onSuccess: () => setТекст('') });
  }

  function вернутьПомощника() {
    if (!canOperate) {
      setЗаметка(
        'Возвращать помощника в переписку может тот, кто её ведёт. Право выдаёт администратор системы.',
      );
      return;
    }
    if (вернуть.isPending) {
      setЗаметка('Помощник уже возвращается, ждём ответ сервера.');
      return;
    }
    setЗаметка(null);
    вернуть.mutate();
  }

  const названияПолей = new Map<string, string>();
  for (const шаг of шаги.data?.items ?? []) {
    if (шаг.slot_code !== null && шаг.slot_code !== '') {
      названияПолей.set(шаг.slot_code, шаг.title);
    }
  }

  return (
    <div className='flex h-full min-h-0 flex-col'>
      <div ref={область} className='min-h-0 flex-1 overflow-y-auto p-5'>
        <div className='flex flex-col gap-3'>
          {журнал.передачи.length > 0 && <ПередачиБлок передачи={журнал.передачи} />}

          <ВыясненноеБлок поля={журнал.поля} названия={названияПолей} />

          <div className='flex flex-col gap-2'>
            <h3 className='text-label-sm text-text-strong-950'>Переписка</h3>
            {/* Причина, гасящая несколько строк сразу, называется ОДИН раз: пояснение про
                служебные сообщения стоит здесь, а не под каждым из них. */}
            {служебныеЕсть && (
              <Hint.Root>
                Служебные сообщения площадки помечены отдельно: это её собственные роботы, помощнику
                они не передаются. Без ответа соискатель при этом не остаётся — на такое отвечает
                рекрутёр.
              </Hint.Root>
            )}
            {лента.length === 0 ? (
              <p className='text-paragraph-sm text-text-sub-600'>
                Сообщений пока нет: диалог создан откликом, но ни одной реплики в нём ещё не было.
              </p>
            ) : (
              <ol className='flex flex-col gap-3'>
                {лента.map((сообщение, индекс) => {
                  const момент = parseMoment(сообщение.created_at);
                  const прежний = parseMoment(лента[индекс - 1]?.created_at ?? null);
                  const новыйДень =
                    момент !== null &&
                    (прежний === null || formatDateDMY(прежний) !== formatDateDMY(момент));
                  return (
                    <li key={сообщение.id} className='flex flex-col gap-2'>
                      {новыйДень && момент !== null && (
                        <span className='self-center text-paragraph-xs text-text-sub-600'>
                          {formatDateDMY(момент)}
                        </span>
                      )}
                      <Сообщение сообщение={сообщение} />
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      </div>

      {/* Нижняя полоса: решение по предложенному ответу и собственный ответ. Белая на сером
          поле, как и шапка, — это форма, а не подложка под парящей кнопкой. */}
      <div className='flex shrink-0 flex-col gap-3 border-t border-stroke-soft-200 bg-bg-white-0 p-5'>
        {!canOperate && (
          <NoPermissionNote
            что='вести эту переписку'
            кто='Переписку ведут рекрутёр и старший рекрутёр — попросите администратора выдать право.'
          />
        )}

        {предложения.length > 0 ? (
          предложения.map((сообщение) => (
            <ПолосаПредложения
              key={сообщение.id}
              сообщение={сообщение}
              подтвердитьИдёт={подтвердить.isPending && подтвердить.variables === сообщение.id}
              отклонитьИдёт={отклонить.isPending && отклонить.variables === сообщение.id}
              onApprove={() =>
                решитьПоПредложению(
                  подтвердить,
                  сообщение.id,
                  'Решение по ответу уже уходит на сервер, ждём его. Второй раз не отправляем.',
                )
              }
              onDiscard={() =>
                решитьПоПредложению(
                  отклонить,
                  сообщение.id,
                  'Решение по ответу уже уходит на сервер, ждём его. Второй раз не отправляем.',
                )
              }
            />
          ))
        ) : (
          <ПочемуТихо
            диалог={диалог}
            настройки={настройки.data?.items}
            неПриехали={настройки.data === undefined && настройки.error !== null}
            canOperate={canOperate}
            canConfigure={canConfigure}
            возвратИдёт={вернуть.isPending}
            onResume={вернутьПомощника}
            onSettings={() =>
              void navigate(openPanelHref(route, PANEL_ASSISTANT_SETTINGS, диалог.account_id))
            }
          />
        )}

        {подтвердить.isError && <ErrorAlert error={подтвердить.error} />}
        {отклонить.isError && <ErrorAlert error={отклонить.error} />}
        {вернуть.isError && <ErrorAlert error={вернуть.error} />}

        <form onSubmit={отправитьСвой} noValidate className='flex flex-col gap-2'>
          <TextArea
            id='hiring-dialog-reply'
            label='Написать соискателю'
            value={текст}
            onChange={setТекст}
            rows={3}
            maxLength={4000}
            placeholder='Ответ соискателю. Уйдёт на площадку от имени компании.'
            error={ошибкаТекста}
            // Последствие называется ДО отправки, а не после (сценарий 100): человек, который
            // вмешался в диалог, должен знать, что дальше помощник молчит, — иначе он будет
            // ждать от него следующего вопроса и не дождётся.
            hint='После вашего сообщения помощник встаёт на паузу по этой переписке и молчит, пока вы не нажмёте «Вернуть помощника».'
          />

          {написать.isError && <ErrorAlert error={написать.error} />}
          {заметка !== null && <GateNote>{заметка}</GateNote>}

          <div className='flex items-center gap-2'>
            <Button.Root type='submit' variant='primary' mode='filled' size='small'>
              {написать.isPending ? 'Отправляем…' : 'Написать соискателю'}
            </Button.Root>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Полоса предложенного ответа: текст, вердикт сторожа и две кнопки с разными последствиями. */
function ПолосаПредложения({
  сообщение,
  подтвердитьИдёт,
  отклонитьИдёт,
  onApprove,
  onDiscard,
}: {
  сообщение: DialogMessage;
  подтвердитьИдёт: boolean;
  отклонитьИдёт: boolean;
  onApprove: () => void;
  onDiscard: () => void;
}) {
  const сторож = разборСторожа(сообщение.сторож);
  return (
    <div className='flex flex-col gap-2 rounded-xl bg-bg-white-0 p-4 ring-1 ring-inset ring-warning-base'>
      <span className='flex flex-wrap items-center gap-2'>
        <RiRobot2Line className='size-4 shrink-0 text-text-soft-400' />
        <span className='text-label-sm text-text-strong-950'>Помощник предлагает ответ</span>
        <Badge.Root variant='lighter' color='orange' size='medium'>
          {сообщение.state_title}
        </Badge.Root>
      </span>

      <ТекстСообщения сообщение={сообщение} />

      {сторож !== null && сторож.прошло && (
        <p className='text-paragraph-xs text-text-sub-600'>
          Сторож исходящего проверил текст: замечаний нет.
        </p>
      )}

      <div className='flex flex-wrap items-center gap-2'>
        <Button.Root type='button' variant='primary' mode='filled' size='small' onClick={onApprove}>
          {подтвердитьИдёт ? 'Отправляем…' : 'Отправить'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onDiscard}>
          {отклонитьИдёт ? 'Отклоняем…' : 'Отклонить'}
        </Button.Root>
      </div>

      <Hint.Root>
        Отклонённое наружу не уходит вовсе: соискатель этого текста не увидит, а переписка перейдёт
        к вам — отвечать придётся самому.
      </Hint.Root>
    </div>
  );
}

/**
 * Почему предложенного ответа нет прямо сейчас. Три ответа, и они не сводятся друг к другу
 * (правило 7). Порядок проверки задан силой: выключенный режим сильнее паузы — при нём
 * возвращать помощника незачем, он всё равно не заговорит.
 */
function ПочемуТихо({
  диалог,
  настройки,
  неПриехали,
  canOperate,
  canConfigure,
  возвратИдёт,
  onResume,
  onSettings,
}: {
  диалог: DialogRow;
  настройки: readonly AssistantSettings[] | undefined;
  неПриехали: boolean;
  canOperate: boolean;
  canConfigure: boolean;
  возвратИдёт: boolean;
  onResume: () => void;
  onSettings: () => void;
}) {
  // Режим ещё неизвестен — не гадаем. Неизвестность это не «помощник работает»: назвать
  // одну из трёх причин наугад значит соврать человеку о том, ждать ему ответа или нет.
  if (настройки === undefined) {
    if (неПриехали) {
      return (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          Режим помощника по этой учётной записи не приехал: связь с сервером прервалась. Пока
          неизвестно, ответит он сам или нет. Обновите экран.
        </Hint.Root>
      );
    }
    return <Skeleton className='h-4 w-2/3' />;
  }

  const запись = настройки.find((строка) => строка.account_id === диалог.account_id);
  if (запись === undefined) {
    return (
      <Hint.Root hasError>
        <Hint.Icon as={RiErrorWarningLine} />
        Настройки помощника по этой учётной записи не нашлись, хотя переписка по ней идёт. Откройте
        раздел «Площадка» и проверьте, на месте ли запись.
      </Hint.Root>
    );
  }

  if (запись.mode === 'off') {
    return (
      <Alert.Root variant='light' status='warning' size='small' role='status'>
        <Alert.Icon as={RiChatOffLine} />
        <div className='flex min-w-0 flex-col gap-1'>
          <span>
            Помощник выключен на учётной записи «{запись.account_title}». Он не ответит ни в этой
            переписке, ни в остальных по этой записи: вся она ведётся руками.
          </span>
          {canConfigure && (
            <div className='flex items-center gap-2'>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xxsmall'
                onClick={onSettings}
              >
                <Button.Icon as={RiSettings3Line} />
                Открыть режим помощника
              </Button.Root>
            </div>
          )}
        </div>
      </Alert.Root>
    );
  }

  if (диалог.на_паузе) {
    return (
      <Alert.Root variant='light' status='information' size='small' role='status'>
        <Alert.Icon as={RiPauseCircleLine} />
        <div className='flex min-w-0 flex-col gap-1'>
          <span>
            В эту переписку написал человек, и помощник молчит по ней до явного возврата. Сам он не
            заговорит, даже когда соискатель ответит.
          </span>
          {canOperate && (
            <div className='flex items-center gap-2'>
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xxsmall'
                onClick={onResume}
              >
                <Button.Icon as={RiArrowGoBackLine} />
                {возвратИдёт ? 'Возвращаем…' : 'Вернуть помощника'}
              </Button.Root>
            </div>
          )}
        </div>
      </Alert.Root>
    );
  }

  return (
    <Hint.Root>
      {запись.mode === 'auto'
        ? 'Предложенных ответов сейчас нет, и в этом режиме их и не бывает: помощник отвечает сам. Сюда попадает только то, что он передал человеку.'
        : 'Предложенных ответов сейчас нет. Помощник готовит ответ после каждого сообщения соискателя — он появится здесь, и наружу уйдёт только после вашего подтверждения.'}
    </Hint.Root>
  );
}

/** Одно сообщение ленты. Автор виден всегда — прямое требование сценария 101. */
function Сообщение({ сообщение }: { сообщение: DialogMessage }) {
  const момент = parseMoment(сообщение.created_at);
  const время = момент === null ? null : formatTimeHM(момент);
  const наше = НАШИ_АВТОРЫ.has(сообщение.author_kind);

  if (сообщение.служебное_площадки) {
    return (
      <div className='flex flex-col items-center gap-0.5 text-center'>
        <span className='text-paragraph-xs text-text-sub-600'>
          {сообщение.author_title}
          {время !== null && ` · ${время}`} · служебное
        </span>
        <div className='max-w-lg'>
          <ТекстСообщения сообщение={сообщение} приглушённо />
        </div>
      </div>
    );
  }

  const сторож = разборСторожа(сообщение.сторож);
  const пояснение = СОСТОЯНИЕ_ПОЯСНЕНИЕ[сообщение.state];

  return (
    <div className={cn('flex', наше ? 'justify-end' : 'justify-start')}>
      <div className='flex max-w-lg flex-col gap-1 rounded-xl bg-bg-white-0 p-3 ring-1 ring-inset ring-stroke-soft-200'>
        <span className='flex flex-wrap items-center gap-2'>
          <span className='text-label-xs text-text-sub-600'>
            {сообщение.author_title}
            {сообщение.author_name !== null && ` · ${сообщение.author_name}`}
          </span>
          {время !== null && <span className='text-paragraph-xs text-text-sub-600'>{время}</span>}
          {/* Состояние отправки показывается только у того, что писали МЫ. У сообщения
              соискателя «Отправлено» отвечало бы на вопрос, которого никто не задавал, и
              путало бы: отправлено кем. */}
          {наше && (
            <Badge.Root
              variant='lighter'
              color={СОСТОЯНИЕ_ЦВЕТ[сообщение.state] ?? 'gray'}
              size='medium'
            >
              {сообщение.state_title}
            </Badge.Root>
          )}
        </span>

        <ТекстСообщения сообщение={сообщение} />

        {пояснение !== undefined && (
          <p className='text-paragraph-xs text-text-sub-600'>{пояснение}</p>
        )}

        {сторож !== null && (
          <p className='text-paragraph-xs text-text-sub-600'>
            {сторож.прошло
              ? 'Сторож исходящего проверил текст: замечаний нет.'
              : `Сторож исходящего: ${сторож.замечания.join('; ')}`}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Тело сообщения. Пустой текст — не пустой пузырь, а сказанный факт: тело стёрли по
 * требованию человека или по сроку хранения, а кто и когда писал — осталось (правило 7).
 */
function ТекстСообщения({
  сообщение,
  приглушённо,
}: {
  сообщение: DialogMessage;
  приглушённо?: boolean;
}) {
  if (сообщение.текст === null || сообщение.текст === '') {
    return (
      <p className='text-paragraph-sm text-text-sub-600'>
        Текст стёрт: по требованию человека или по сроку хранения. Кто писал и когда — осталось.
      </p>
    );
  }
  return (
    <p
      className={cn(
        'whitespace-pre-wrap text-paragraph-sm',
        приглушённо === true ? 'text-text-sub-600' : 'text-text-strong-950',
      )}
    >
      {сообщение.текст}
    </p>
  );
}

/** Что помощник и рекрутёр уже выяснили о соискателе и КЕМ проставлено (сценарии 97 и 101). */
function ВыясненноеБлок({
  поля,
  названия,
}: {
  поля: readonly DialogField[];
  названия: ReadonlyMap<string, string>;
}) {
  return (
    <Block title='Что выяснено'>
      {поля.length === 0 ? (
        <p className='text-paragraph-sm text-text-sub-600'>
          Пока ничего: ни один вопрос сценария ещё не получил ответа. Значения появятся здесь по
          ходу разговора, и у каждого будет видно, кем оно проставлено.
        </p>
      ) : (
        <dl className='flex flex-col'>
          {поля.map((поле) => (
            <FieldRow
              key={поле.id}
              // Код поля — служебное обозначение, человеку оно не говорит ничего. Вместо него
              // стоит вопрос сценария, которым это поле заполняют; голый код остаётся только
              // там, где шага с таким слотом в сценарии уже нет.
              label={названия.get(поле.field_code) ?? поле.field_code}
              value={поле.value_text === null || поле.value_text === '' ? null : поле.value_text}
              missing='ответа нет: вопрос задан, но значение не разобрано'
              note={откудаЗначение(поле)}
            />
          ))}
        </dl>
      )}
    </Block>
  );
}

/** Кем проставлено значение: моделью или человеком, и с какой уверенностью. */
function откудаЗначение(поле: DialogField): string {
  const части: string[] = [поле.source_title];
  if (поле.set_by_name !== null && поле.set_by_name !== '') {
    части.push(поле.set_by_name);
  }
  const доля = вПроценты(поле.confidence);
  if (доля !== null) {
    части.push(`уверенность ${доля} %`);
  } else if (поле.source !== 'operator') {
    // Ноль и «не записано» — разные факты: по первому помощник промолчал бы, второй означает
    // лишь, что значение проставлено до того, как уверенность стали хранить.
    части.push('уверенность не записана');
  }
  return части.join(' · ');
}

/** Передачи человеку: причина из справочника семи правил, когда и закрыта ли. */
function ПередачиБлок({ передачи }: { передачи: readonly DialogHandoff[] }) {
  // Открытые вперёд: закрытые — история, открытая ждёт рекрутёра прямо сейчас.
  const порядок = [...передачи].sort((левая, правая) => {
    const слева = левая.resolved_at === null ? 0 : 1;
    const справа = правая.resolved_at === null ? 0 : 1;
    return слева - справа;
  });
  const открытых = передачи.filter((передача) => передача.resolved_at === null).length;

  return (
    <Block title='Передано человеку'>
      <div className='flex flex-col gap-2'>
        {открытых > 0 && (
          <Hint.Root>
            Передача ждёт вас: помощник по этой переписке остановился сам. Ответьте соискателю —
            передача закроется вашим сообщением.
          </Hint.Root>
        )}
        <ol className='flex flex-col'>
          {порядок.map((передача) => {
            const заведена = parseMoment(передача.created_at);
            const закрыта = parseMoment(передача.resolved_at);
            return (
              <li
                key={передача.id}
                className='flex flex-col gap-0.5 border-b border-stroke-soft-200 py-2 last:border-b-0'
              >
                <span className='flex flex-wrap items-center gap-2'>
                  <span className='text-label-sm text-text-strong-950'>
                    {передача.reason_title}
                  </span>
                  <Badge.Root
                    variant='lighter'
                    color={передача.resolved_at === null ? 'orange' : 'gray'}
                    size='medium'
                  >
                    {передача.resolved_at === null ? 'Не закрыта' : 'Закрыта'}
                  </Badge.Root>
                </span>
                {передача.detail !== null && передача.detail !== '' && (
                  <span className='text-paragraph-sm text-text-sub-600'>{передача.detail}</span>
                )}
                <span className='text-paragraph-xs text-text-sub-600'>
                  {заведена === null
                    ? 'время передачи не записано'
                    : `передана ${formatDateDMY(заведена)}, ${formatTimeHM(заведена)}`}
                  {закрыта !== null &&
                    ` · закрыта ${formatDateDMY(закрыта)}, ${formatTimeHM(закрыта)}`}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </Block>
  );
}

/** Доля от нуля до единицы в целые проценты. Мусор и пустое — null, а не «0 %». */
function вПроценты(значение: string | null | undefined): number | null {
  if (значение === null || значение === undefined || значение === '') {
    return null;
  }
  const число = Number(значение);
  return Number.isFinite(число) ? Math.round(число * 100) : null;
}

/**
 * Вердикт сторожа исходящего в читаемый вид. Приходит свободным объектом
 * (`{ прошло, замечания: [{ правило, подробность }] }`), поэтому разбирается защитно: чужая
 * форма не имеет права уронить показ переписки.
 */
function разборСторожа(
  значение: Record<string, unknown> | null | undefined,
): { прошло: boolean; замечания: string[] } | null {
  if (значение === null || значение === undefined) {
    return null;
  }
  const замечания: string[] = [];
  const сырые = значение['замечания'];
  if (Array.isArray(сырые)) {
    for (const строка of сырые) {
      if (typeof строка !== 'object' || строка === null) {
        continue;
      }
      const запись = строка as Record<string, unknown>;
      const слова = [запись['правило'], запись['подробность']].filter(
        (часть): часть is string => typeof часть === 'string' && часть !== '',
      );
      if (слова.length > 0) {
        замечания.push(слова.join(': '));
      }
    }
  }
  const прошло = значение['прошло'] === true;
  // «Не прошло» без единого замечания сказать нечем: молчаливая красная строка хуже её
  // отсутствия, потому что не отвечает на вопрос «а что не так».
  if (!прошло && замечания.length === 0) {
    return null;
  }
  return { прошло, замечания };
}
